import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Flex_Responsive() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📱 Flexbox Responsive Layouts</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Flexbox is perfect for creating layouts that automatically adjust to
        different screen sizes. Using flex container and flex item properties
        combined with media queries, you can create responsive rows, columns,
        grids, and card layouts.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>flex-wrap: wrap</code> to allow items to move to the next
            line on smaller screens.
          </li>
          <li>
            Combine <code>flex-basis</code> or <code>flex: 1 1 auto</code> for
            dynamic item sizing.
          </li>
          <li>
            Use media queries to change <code>flex-direction</code>,{" "}
            <code>gap</code>, or <code>padding</code> at different breakpoints.
          </li>
          <li>
            Align items using <code>justify-content</code> and{" "}
            <code>align-items</code> responsively.
          </li>
        </ul>
      </div>

      {/* Example 1: Responsive Row Wrap */}
      <h2 className="section-title">1️⃣ Responsive Row Wrap</h2>
      <CodeCopyBlock
        code={`.flex-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}
.flex-item {
  flex: 1 1 200px;
  padding: 20px;
  border-radius: 8px;
  color: white;
}
@media(max-width:600px){
  .flex-container {
    flex-direction: column;
  }
}`}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            flex: "1 1 200px",
            background: "#3b82f6",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 1
        </div>
        <div
          style={{
            flex: "1 1 200px",
            background: "#2563eb",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 2
        </div>
        <div
          style={{
            flex: "1 1 200px",
            background: "#60a5fa",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 3
        </div>
        <div
          style={{
            flex: "1 1 200px",
            background: "#1e40af",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Box 4
        </div>
      </div>

      {/* Example 2: Responsive Column Change */}
      <h2 className="section-title">2️⃣ Column Layout Switch</h2>
      <CodeCopyBlock
        code={`.flex-column {
  display: flex;
  flex-direction: row;
  gap: 10px;
}
@media(max-width:500px){
  .flex-column {
    flex-direction: column;
  }
}`}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "#f97316",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Item A
        </div>
        <div
          style={{
            flex: 1,
            background: "#ea580c",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Item B
        </div>
        <div
          style={{
            flex: 1,
            background: "#fbbf24",
            color: "white",
            padding: "30px",
            borderRadius: "8px",
          }}
        >
          Item C
        </div>
      </div>

      {/* Example 3: Card Grid */}
      <h2 className="section-title">3️⃣ Responsive Card Grid</h2>
      <CodeCopyBlock
        code={`.card-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}
.card {
  flex: 1 1 250px;
  padding: 20px;
  background: #10b981;
  color: white;
  border-radius: 10px;
}`}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "2rem",
        }}
      >
        <div
          style={{
            flex: "1 1 250px",
            background: "#10b981",
            color: "white",
            padding: "30px",
            borderRadius: "10px",
          }}
        >
          Card 1
        </div>
        <div
          style={{
            flex: "1 1 250px",
            background: "#059669",
            color: "white",
            padding: "30px",
            borderRadius: "10px",
          }}
        >
          Card 2
        </div>
        <div
          style={{
            flex: "1 1 250px",
            background: "#047857",
            color: "white",
            padding: "30px",
            borderRadius: "10px",
          }}
        >
          Card 3
        </div>
        <div
          style={{
            flex: "1 1 250px",
            background: "#065f46",
            color: "white",
            padding: "30px",
            borderRadius: "10px",
          }}
        >
          Card 4
        </div>
      </div>

      {/* Example 4: Responsive Navbar */}
      <h2 className="section-title">4️⃣ Responsive Navbar</h2>
      <CodeCopyBlock
        code={`.navbar {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  background: #3b82f6;
  padding: 10px;
}
.nav-item {
  padding: 10px;
  color: white;
}`}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          background: "#3b82f6",
          padding: "10px",
          marginBottom: "2rem",
        }}
      >
        <span style={{ color: "white", fontWeight: "bold" }}>Logo</span>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ color: "white", padding: "10px" }}>Home</span>
          <span style={{ color: "white", padding: "10px" }}>About</span>
          <span style={{ color: "white", padding: "10px" }}>Services</span>
          <span style={{ color: "white", padding: "10px" }}>Contact</span>
        </div>
      </div>

      {/* Example 5: Image Gallery */}
      <h2 className="section-title">5️⃣ Responsive Image Gallery</h2>
      <CodeCopyBlock
        code={`.gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.gallery img {
  flex: 1 1 200px;
  width: 100%;
  border-radius: 8px;
}`}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <img
          src="https://picsum.photos/id/1011/300/200"
          style={{ flex: "1 1 200px", borderRadius: "8px" }}
          alt="Gallery 1"
        />
        <img
          src="https://picsum.photos/id/1015/300/200"
          style={{ flex: "1 1 200px", borderRadius: "8px" }}
          alt="Gallery 2"
        />
        <img
          src="https://picsum.photos/id/1016/300/200"
          style={{ flex: "1 1 200px", borderRadius: "8px" }}
          alt="Gallery 3"
        />
        <img
          src="https://picsum.photos/id/1025/300/200"
          style={{ flex: "1 1 200px", borderRadius: "8px" }}
          alt="Gallery 4"
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine flex-wrap with media queries for fully adaptive layouts.
          </li>
          <li>
            Experiment with <code>flex-basis</code> and <code>flex-grow</code>{" "}
            for dynamic item sizing.
          </li>
          <li>
            Create responsive cards, galleries, and navbars using flex
            containers.
          </li>
          <li>Try nested flex containers for complex UI patterns.</li>
          <li>
            Test layouts on multiple devices or browser resize to see
            responsiveness in action.
          </li>
        </ul>
      </div>
    </div>
  );
}
